/**
 * Centralized date formatters for pt-BR locale.
 * ALL date formatting across the app should use these helpers
 * instead of importing date-fns directly in components.
 */

import { format } from 'date-fns';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { nowBrazil } from '@/lib/brazilTime';

/** Short date: 01/04/2026 */
export function formatDateShort(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/** Short date with time: 01/04, 14:30 */
export function formatDateTimeShort(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Relative date: "14:30" (today), "Ontem", "seg." (this week), "01/04" (older) */
export function formatDateRelative(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  } else if (diffDays === 1) {
    return 'Ontem';
  } else if (diffDays < 7) {
    return date.toLocaleDateString('pt-BR', { weekday: 'short' });
  } else {
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  }
}

/** Full date+time: "01/04/2026 às 14:30" */
export function formatDateTime(dateStr: string): string {
  return format(new Date(dateStr), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
}

/** Compact date+time: "01/04/26 14:30" */
export function formatDateCompact(dateStr: string): string {
  return format(new Date(dateStr), "dd/MM/yy HH:mm", { locale: ptBR });
}

/** Compact date+time with "às": "01/04/26 às 14:30" */
export function formatDateCompactAt(dateStr: string): string {
  return format(new Date(dateStr), "dd/MM/yy 'às' HH:mm", { locale: ptBR });
}

/** Date+time without year: "01/04 14:30" */
export function formatDateTimeNoYear(dateStr: string): string {
  return format(new Date(dateStr), "dd/MM HH:mm", { locale: ptBR });
}

/** Time then date: "14:30 · 01/04" */
export function formatTimeThenDate(dateStr: string): string {
  return format(new Date(dateStr), "HH:mm · dd/MM");
}

/** Long month: "01 de abr de 2026" */
export function formatDateMonth(dateStr: string): string {
  return format(new Date(dateStr), "dd 'de' MMM 'de' yyyy", { locale: ptBR });
}

/** Full date+time no "às": "01/04/2026 14:30" */
export function formatDateTimeFull(dateStr: string): string {
  return format(new Date(dateStr), "dd/MM/yyyy HH:mm", { locale: ptBR });
}

/** Relative distance: "há 2 dias", "há 5 minutos" */
export function formatRelativeDistance(dateStr: string): string {
  return formatDistanceToNow(new Date(dateStr), { addSuffix: true, locale: ptBR });
}

/** ISO date for form inputs: "2026-04-01" */
export function formatDateISO(date: Date = new Date()): string {
  return format(date, 'yyyy-MM-dd');
}

// =============================================
// Compact time-ago (previously in formatTimeAgo.ts)
// =============================================

/** Compact time-ago: "agora", "há 5min", "há 2h", "ontem", "há 3d", "01 abr" */
export function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'agora';
  if (diffMins < 60) return `há ${diffMins}min`;
  if (diffHours < 24) return `há ${diffHours}h`;
  if (diffDays === 1) return 'ontem';
  if (diffDays < 7) return `há ${diffDays}d`;
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
}

// =============================================
// Chat/community date helpers (previously in comunidade/utils/dateFormatters.ts)
// =============================================

/** Date label: "Hoje", "Ontem", or full date */
export function formatDateLabel(d: Date): string {
  const today = nowBrazil();
  const yesterday = nowBrazil();
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Hoje';
  if (d.toDateString() === yesterday.toDateString()) return 'Ontem';
  return d.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' });
}

/** Message date: "Hoje às 14:30", "Ontem às 10:00", or "01/04/2026 14:30" */
export function formatMessageDate(dateStr: string): string {
  const d = new Date(dateStr);
  const today = nowBrazil();
  const time = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  if (d.toDateString() === today.toDateString()) return `Hoje às ${time}`;
  const yesterday = nowBrazil();
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return `Ontem às ${time}`;
  return `${d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })} ${time}`;
}

/** Returns date separator label if date changed between two messages */
export function getDateSeparator(prevDate: string | null, currentDate: string): string | null {
  const curr = new Date(currentDate);
  if (!prevDate) return formatDateLabel(curr);
  const prev = new Date(prevDate);
  if (prev.toDateString() !== curr.toDateString()) return formatDateLabel(curr);
  return null;
}

/** Check if time difference between two dates exceeds 5 minutes */
export function isTimeDiffLarge(a: string, b: string): boolean {
  return Math.abs(new Date(b).getTime() - new Date(a).getTime()) > 5 * 60 * 1000;
}

/** Safe time-ago with fallback for invalid/null values */
export function safeTimeAgo(value?: string | null): string {
  if (!value) return 'agora';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'agora';
  return formatDistanceToNow(d, { addSuffix: true, locale: ptBR });
}
